import axios from 'axios';
import { clearAuthData } from '../utils/auth';

const API_BASE_URL = process.env['REACT_APP_API_BASE_URL'];

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: params => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => sp.append(key, v));
      } else if (value !== null && value !== undefined) {
        sp.append(key, value);
      }
    });
    return sp.toString();
  },
});
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);
const shouldRedirectToLogin = error => {
  if (axios.isCancel(error)) return false;
  const status = error.response?.status;
  const requestUrl = error.config?.url ?? '';
  const isAuthMe =
    requestUrl.includes('auth/me') ||
    (error.config?.baseURL + requestUrl).includes('auth/me');

  if (status === 401 || (status === 400 && isAuthMe)) {
    return true;
  }

  if (!error.response) {
    const hadToken = !!localStorage.getItem('token');
    return hadToken;
  }

  return false;
};

const SESSION_TIMEOUT_KEY = 'sessionTimeout';

apiClient.interceptors.response.use(
  response => response,
  error => {
    if (shouldRedirectToLogin(error)) {
      sessionStorage.setItem(SESSION_TIMEOUT_KEY, 'true');
      clearAuthData();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);
const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  client: apiClient,
  ENDPOINTS: {
    AUTH: {
      ME: '/api/auth/me',
      LOGOUT: '/api/auth/logout',
      FORGOT_PASSWORD: '/api/auth/forgot-password',
      RESET_PASSWORD: '/api/auth/reset-password',
      JOIN: '/api/auth/join',
      GOOGLE_OAUTH: '/oauth2/authorization/google',
      ALL_USERS: '/api/v1/users/search',
    },
    ANNOUNCEMENTS: {
      LIST: '/api/v1/announcements',
      SEARCH: '/api/v1/announcements/search',
      DETAIL: id => `/api/v1/announcements/${id}`,
      CREATE: '/api/v1/announcements',
      UPDATE: id => `/api/v1/announcements/${id}`,
      DELETE: id => `/api/v1/announcements/${id}`,
    },
    ATTENDANCE: {
      LIST: '/api/v1/holidays/current-year',
      UPCOMING: '/api/v1/holidays/upcoming',
      UPCOMING_YEAR: year => `/api/v1/holidays/upcoming-year/${year}`,
      GET_ALL_HOLIDAYS: '/api/v1/holidays/get-all-holidays',
      DETAIL: id => `/api/v1/holidays/${id}`,
      CREATE: '/api/v1/holidays',
      UPDATE: id => `/api/v1/holidays/${id}`,
      DELETE: id => `/api/v1/holidays/${id}`,
      TYPES: '/api/v1/holidays/types',
    },
    TIMESHEET: {
      CALENDAR: '/api/v1/timesheet/calendar',
      ALLOCATIONS: '/api/v1/timesheet/allocations',
      ACTIVITIES: '/api/v1/timesheet/activities',
      ENTRIES: '/api/v1/timesheet/entries',
      ENTRY: id => `/api/v1/timesheet/entries/${id}`,
      SUBMIT_ENTRIES: '/api/v1/timesheet/entries/submit',
      RECALL_ENTRIES: '/api/v1/timesheet/entries/recall',
      COPY_ENTRIES: '/api/v1/timesheet/entries/copy',
      MY_REQUESTS: '/api/v1/timesheet/submissions/my-requests',
      SUBMISSION_DETAIL: id => `/api/v1/timesheet/submissions/${id}`,
      APPROVALS_PENDING: '/api/v1/timesheet/approvals/pending',
      APPROVE: '/api/v1/timesheet/approvals/approve',
      REJECT: '/api/v1/timesheet/approvals/reject',
      ELIGIBLE_APPROVERS: '/api/v1/timesheet/approvals/eligible-approvers',
      CHANGE_APPROVER: '/api/v1/timesheet/approvals/change-approver',
      OVERSIGHT_SUBMISSIONS: '/api/v1/timesheet/oversight/submissions',
      OVERSIGHT_SUBMISSIONS_STATUS:
        '/api/v1/timesheet/oversight/submissions/status',
    },
    UTILIZATION_REPORT: {
      ENTRIES: '/api/v1/utilizationreport/entries',
      SUMMARY: '/api/v1/utilizationreport/summary',
      BARDATA: '/api/v1/utilizationreport/bardata',
    },
    FILES: {
      ANNOUNCEMENT_ATTACHMENT: '/api/v1/files/announcements',
    },
    USERS: {
      GET_LATEST_EMPLOYEE_ID: '/api/v1/users/latest-employee-id',
      LIST: '/api/v1/users/search',
      SEARCH: '/api/v1/users/search',
      REPORT: '/api/v1/users/report',
      DETAIL: id => `/api/v1/users/${id}`,
      PROFILE: '/api/v1/users/profile',
      GET_USER_PROFILE: id => `/api/v1/users/${id}`,
      GET_BASIC_USER_PROFILE: id => `/api/v1/users/basic/${id}`,
      UPDATE_USER_PROFILE: id => `/api/v1/users/${id}`,
      UPDATE_USER_PROFILE_USER: id => `/api/v1/users/profile/${id}`,
      UPLOAD_PROFILE_PICTURE: id => `/api/v1/users/${id}/profile-picture`,
      CREATE_USER: '/api/v1/users',
      GET_DEPARTMENTS: '/api/v1/users/departments',
      GET_DESIGNATIONS: '/api/v1/users/designations',
      GET_EMPLOYEE_TYPES: '/api/v1/users/employee-types',
      GET_WORK_LOCATIONS: '/api/v1/users/worklocations',
      GET_REPORTING_MANAGERS: '/api/v1/users/reporting-managers',
      GET_PERFORMANCE_STATUSES: '/api/v1/performance-dashboard/statuses',
      ROLES: '/api/v1/users/roles',
      REMOVE_ROLE: (userId, role) => `/api/v1/users/${userId}/roles/${role}`,
      ROLES_COUNT: '/api/v1/users/roles/count',
      DELETE_USER: id => `/api/v1/users/${id}`,
      BY_ROLE: role => `/api/v1/users/by-role/${role}`,
      BY_ROLE_PAGINATED: role => `/api/v1/users/by-role/${role}/paginated`,
    },
    ASSETS: {
      LIST: '/api/v1/assets',
      GET_BY_ID: id => `/api/v1/assets/${id}`,
      ISSUED_ASSET: id => `/api/v1/assets/issued-asset/${id}`,
      SEARCH: '/api/v1/assets/search',
      CREATE: '/api/v1/assets',
      UPDATE: id => `/api/v1/assets/${id}`,
      STATUS_OPTIONS: '/api/v1/assets/status-options',
      STATUSES: '/api/v1/assets/statuses',
      ALLOCATION_STATUS: '/api/v1/assets/allocation-status',
      TYPES: '/api/v1/assets/asset-types',
      LATEST_ASSET_CODE: '/api/v1/assets/latest-asset-code',
      DISK_CAPACITY: '/api/v1/assets/hard-disk-capacity',
      HARD_DISK_TYPES: '/api/v1/assets/hard-disk-type',
      RAM_TYPES: '/api/v1/assets/ram-types',
      RAM_CAPACITIES: '/api/v1/assets/ram-capacity',
      RAM_SLOTS: '/api/v1/assets/ram-slot',
      RAM_SLOTS_OCCUPIED: '/api/v1/assets/ram-slot-occupied',
      CONDITION_STATUS: '/api/v1/assets/condition-status',
      ANTIVIRUS_OPTIONS: '/api/v1/assets/antivirus',
      OS_TYPES: '/api/v1/assets/os-types',
      CURRENCY_TYPES: '/api/v1/assets/currency-type',
      BRANDS: '/api/v1/assets/brands',
      USER_ASSETS: userId => `/api/v1/assets/user/${userId}`,
      CREATE_RETURN_REQUEST: '/api/v1/assets/create-request',
      GET_RETURN_REQUEST: assetId => `/api/v1/assets/return-request/${assetId}`,
      APPROVE_RETURN_REQUEST: id => `/api/v1/assets/${id}/approve`,
      REQUEST_ASSET: '/api/v1/asset-requests',
      REQUESTED_ASSETS: '/api/v1/assets/requested-asset',
      APPROVE_ASSET_REQUEST: requestId =>
        `/api/v1/assets/${requestId}/approve-assetrequest`,
      MANAGER_VIEW: requestId =>
        `/api/v1/asset-requests/manager-view/${requestId}`,
      EMPLOYEE_VIEW: requestId =>
        `/api/v1/asset-requests/employee-view/${requestId}`,
      REQUEST_STATUS: '/api/v1/assets/request-status',
      EMPLOYEE_REQUEST_STATUS: '/api/v1/assets/employee-request-status',
      APPROVER_REQUEST_STATUS_IT_ADMIN:
        '/api/v1/assets/approver-request-status/it-admin',
      APPROVER_REQUEST_STATUS_MANAGER:
        '/api/v1/assets/approver-request-status/manager',
      PRIORITY_STATUS: '/api/v1/assets/priority-status',
      MANAGER_ACTION: requestId =>
        `/api/v1/asset-requests/${requestId}/manager-action`,
      ADMIN_VIEW: requestId => `/api/v1/asset-requests/admin-view/${requestId}`,
      AVAILABLE_ASSETS: requestId =>
        `/api/v1/asset-requests/${requestId}/available-assets`,
      ALLOCATE_BY_IT_ADMIN: requestId =>
        `/api/v1/asset-requests/${requestId}/allocateByItAdmin`,
      ESIGN_VIEW: fileName => `/api/v1/assets/e-sign/view/${fileName}`,
      ESIGN_DOWNLOAD: fileName => `/api/v1/assets/e-sign/download/${fileName}`,
    },
    ASSET_DASHBOARD: {
      COMPANY_ASSETS: '/api/v1/asset-dashboard/company-assets',
      DELETED_COMPANY_ASSETS: '/api/v1/asset-dashboard/company-assets/deleted',
      ASSET_REQUESTS: '/api/v1/asset-dashboard/asset-requests',
      MY_ISSUED_ASSETS: '/api/v1/asset-dashboard/my-issued-assets',
      MY_ASSET_REQUESTS: '/api/v1/asset-dashboard/my-asset-requests',
      DELETE_COMPANY_ASSET: assetId =>
        `/api/v1/asset-dashboard/company-assets/${assetId}`,
      DOWNLOAD_COMPANY_ASSETS:
        '/api/v1/asset-dashboard/company-assets/download',
      MANAGER_APPROVALS: '/api/v1/asset-dashboard/manager-approvals',
      IT_APPROVALS: '/api/v1/asset-dashboard/it-approvals',
      DOWNLOAD_IT_APPROVALS: '/api/v1/asset-dashboard/it-approvals/download',
    },
    REWARDS: {
      LIST: '/api/v1/rewards',
      DETAIL: id => `/api/v1/rewards/${id}`,
      CREATE: '/api/v1/rewards',
      UPDATE: id => `/api/v1/rewards/${id}`,
      DELETE: id => `/api/v1/rewards/${id}`,
    },
    SURVEYS: {
      LIST: '/api/v1/surveys',
      DETAIL: id => `/api/v1/surveys/${id}`,
      SUBMIT: id => `/api/v1/surveys/${id}/submit`,
    },
    POLICIES: {
      FILES: '/api/v1/policies/files',
    },

    BIRTHDAY_CARDS: {
      TODAY: '/api/v1/birthday-cards/today',
      BY_DATE: date => `/api/v1/birthday-cards/date/${date}`,
      DETAIL: cardId => `/api/v1/birthday-cards/${cardId}`,
      ADD_WISH: cardId => `/api/v1/birthday-cards/${cardId}/wishes`,
      DASHBOARD_TODAY: '/api/v1/birthday-cards/dashboard/today',
      CREATE_TODAY: '/api/v1/birthday-cards/create-today',
      REGENERATE: userId => `/api/v1/birthday-cards/regenerate/${userId}`,
    },
    EXPENSE_MANAGEMENT: {
      LIST: '/api/v1/expense-requests/list',
      CREATE: '/api/v1/expense-requests',
      UPDATE: id => `/api/v1/expense-requests/${id}`,
      SUBMIT: id => `/api/v1/expense-requests/${id}/submit`,
      DELETE: id => `/api/v1/expense-requests/${id}`,
      DETAIL: id => `/api/v1/expense-requests/${id}`,
      UPLOAD_ATTACHMENT: id => `/api/v1/expense-requests/${id}/attachments`,
      DELETE_ATTACHMENT: (id, fileId) =>
        `/api/v1/expense-requests/${id}/attachments/${fileId}`,
      DOWNLOAD_ATTACHMENT: (id, fileId) =>
        `/api/v1/expense-requests/${id}/attachments/${fileId}`,
      AUDIT_LOG: id => `/api/v1/expense-requests/${id}/logs`,

      MANAGER_LIST: '/api/v1/manager/expense-requests/list',
      MANAGER_DETAIL: id => `/api/v1/manager/expense-requests/${id}`,
      MANAGER_ACTION: id => `/api/v1/manager/expense-requests/${id}/action`,
      MANAGER_CHANGE_APPROVER: id =>
        `/api/v1/manager/expense-requests/${id}/change-approver`,

      ACCOUNTS_LIST: '/api/v1/accounts/expense-requests/list',
      ACCOUNTS_DOWNLOAD: '/api/v1/accounts/expense-requests/download',
      ACCOUNTS_DETAIL: id => `/api/v1/accounts/expense-requests/${id}`,
      ACCOUNTS_ACTION: id => `/api/v1/accounts/expense-requests/${id}/action`,
      ACCOUNTS_DELETE: id => `/api/v1/accounts/expense-requests/${id}`,

      META_PROJECTS: '/api/v1/meta/projects',
      META_EXPENSE_TYPES: '/api/v1/meta/expense-types',
      META_MANAGERS: '/api/v1/meta/managers',
      META_EXPENSE_STATUSES: '/api/v1/meta/expense-statuses',
    },
    LEAVE_MANAGEMENT: {
      // Phase 3 — Metadata & balances (Live)
      META_LEAVE_FORM: '/api/v1/leave/meta/leave-request-form',
      META_COMPOFF_FORM: '/api/v1/leave/meta/comp-off-request-form',
      META_LEAVE_CATEGORIES: '/api/v1/leave/meta/leave-categories',
      META_OPTIONAL_HOLIDAYS: '/api/v1/leave/meta/optional-holidays/available',
      BALANCE_SUMMARY: '/api/v1/leave/balances/summary',
      BALANCE_DETAILS: '/api/v1/leave/balances/details',
      BALANCE_CATEGORY: code => `/api/v1/leave/balances/details/${code}`,
      BALANCE_COMPOFF_DETAILS: '/api/v1/leave/balances/comp-off/details',

      // Phase 4 — Employee leave requests (Planned)
      LIST: '/api/v1/leave/requests/list',
      CREATE: '/api/v1/leave/requests',
      UPDATE: id => `/api/v1/leave/requests/${id}`,
      DELETE: id => `/api/v1/leave/requests/${id}`,
      SUBMIT: id => `/api/v1/leave/requests/${id}/submit`,
      DETAIL: id => `/api/v1/leave/requests/${id}`,
      LOGS: id => `/api/v1/leave/requests/${id}/logs`,
      PREVIEW: '/api/v1/leave/requests/preview',

      // Phase 5 — Employee comp-off requests (Planned)
      COMPOFF_LIST: '/api/v1/leave/comp-off-requests/list',
      COMPOFF_CREATE: '/api/v1/leave/comp-off-requests',
      COMPOFF_UPDATE: id => `/api/v1/leave/comp-off-requests/${id}`,
      COMPOFF_DELETE: id => `/api/v1/leave/comp-off-requests/${id}`,
      COMPOFF_SUBMIT: id => `/api/v1/leave/comp-off-requests/${id}/submit`,
      COMPOFF_DETAIL: id => `/api/v1/leave/comp-off-requests/${id}`,
      COMPOFF_LOGS: id => `/api/v1/leave/comp-off-requests/${id}/logs`,

      // Phase 6 — Manager approval
      MANAGER_LIST: '/api/v1/manager/leave/requests/pending',
      MANAGER_COMPOFF_LIST: '/api/v1/manager/leave/comp-off-requests/pending',
      MANAGER_DETAIL: id => `/api/v1/manager/leave/requests/${id}`,
      MANAGER_COMPOFF_DETAIL: id =>
        `/api/v1/manager/leave/comp-off-requests/${id}`,
      MANAGER_ACTION: id => `/api/v1/manager/leave/requests/${id}/action`,
      MANAGER_COMPOFF_ACTION: id =>
        `/api/v1/manager/leave/comp-off-requests/${id}/action`,
      MANAGER_REASSIGN: id => `/api/v1/manager/leave/requests/${id}/reassign`,
      MANAGER_COMPOFF_REASSIGN: id =>
        `/api/v1/manager/leave/comp-off-requests/${id}/reassign`,

      // Phase 7 — Dashboard widget (Planned)
      DASHBOARD_SUMMARY: '/api/v1/leave/dashboard/summary',

      // Reporting structure + team leave calendar (manager view)
      REPORTING_STRUCTURE: '/api/v1/leave/calendar/reporting-structure',
      CALENDAR: '/api/v1/leave/calendar',
      // Team leave calendar (reporting manager view)
      CALENDAR_MANAGER: '/api/v1/leave/calendar/manager',
      // Org-wide employee leave calendar (HR admin view)
      CALENDAR_ADMIN: '/api/v1/leave/calendar/admin',

      // HR admin — employee leaves dashboard (list of active employees)
      HR_EMPLOYEES: '/api/v1/hr/leave/employees',
      // HR admin — leave balance report grid (employee × year × leave category)
      HR_BALANCE_REPORT: '/api/v1/hr/leave/balance-report',
      // Filter-option lookups for the employee-leaves dashboard
      HR_EMPLOYEE_FILTERS: '/api/v1/hr/leave/employees/filters',
      // Calendar years an employee has an active employment record for
      HR_EMPLOYEE_YEARS: employeeId =>
        `/api/v1/hr/leave/employees/${employeeId}/years`,
      // Leave-balance details (all categories) for a year — ?year=YYYY
      HR_EMPLOYEE_BALANCE: employeeId =>
        `/api/v1/hr/leave/employees/${employeeId}/balance`,
      // Single leave-category balance detail (GET) + entitlement edit (PUT).
      // ?year=YYYY, code ∈ EL|CL|SL|CPF|OHL|LOP
      HR_EMPLOYEE_CATEGORY_BALANCE: (employeeId, categoryCode) =>
        `/api/v1/hr/leave/employees/${employeeId}/balance/${categoryCode}`,
      // Comp Off Details tab — availed + balance (+ credit windows). ?year=YYYY
      HR_EMPLOYEE_COMPOFF: employeeId =>
        `/api/v1/hr/leave/employees/${employeeId}/comp-off`,
      // Leave requests dashboard — per-employee (employeeId+year) or org-wide.
      HR_LEAVE_REQUESTS: '/api/v1/hr/leave/leave-requests',
      // Comp-off earn requests dashboard — same shape as leave-requests.
      HR_COMPOFF_REQUESTS: '/api/v1/hr/leave/comp-off-requests',
      // Export leave requests (same filter body) → downloadable report file.
      HR_LEAVE_REQUESTS_EXPORT: '/api/v1/hr/leave/requests/export',
      // Full detail of one request — ?type=LEAVE|COMP_OFF (required).
      HR_REQUEST_DETAIL: requestId => `/api/v1/hr/leave/requests/${requestId}`,
      // Approve / reject / cancel a request — ?type=LEAVE|COMP_OFF (required).
      HR_REQUEST_STATUS: requestId =>
        `/api/v1/hr/leave/requests/${requestId}/status`,
    },
    PROJECTS: {
      LIST: '/api/v1/projects/list',
      CREATE: '/api/v1/projects',
      DETAIL: id => `/api/v1/projects/${id}`,
      UPDATE: id => `/api/v1/projects/${id}`,
      // §3 — hard-deletes only when the project has no dependents; otherwise
      // rejected outright (400) and the caller offers Cancel instead (the
      // same UPDATE call above, with `projectStatusId` set to Cancelled).
      DELETE: id => `/api/v1/projects/${id}`,
      HISTORY: id => `/api/v1/projects/${id}/history`,
      TEAM_MEMBERS: projectId => `/api/v1/projects/${projectId}/team-members`,
      TEAM_MEMBERS_LIST: projectId =>
        `/api/v1/projects/${projectId}/team-members/list`,
      // §6 — a dedicated export endpoint (not the list endpoint + a
      // download flag, unlike Sprint/User Story).
      TEAM_MEMBERS_DOWNLOAD: projectId =>
        `/api/v1/projects/${projectId}/team-members/download`,
      TEAM_MEMBER: (projectId, teamMemberId) =>
        `/api/v1/projects/${projectId}/team-members/${teamMemberId}`,
      TEAM_MEMBER_HISTORY: (projectId, teamMemberId) =>
        `/api/v1/projects/${projectId}/team-members/${teamMemberId}/history`,
      SPRINTS: {
        LIST: projectId => `/api/v1/projects/${projectId}/sprints/list`,
        CREATE: projectId => `/api/v1/projects/${projectId}/sprints`,
        DETAIL: (projectId, sprintId) =>
          `/api/v1/projects/${projectId}/sprints/${sprintId}`,
        UPDATE: (projectId, sprintId) =>
          `/api/v1/projects/${projectId}/sprints/${sprintId}`,
        DELETE: (projectId, sprintId) =>
          `/api/v1/projects/${projectId}/sprints/${sprintId}`,
        HISTORY: (projectId, sprintId) =>
          `/api/v1/projects/${projectId}/sprints/${sprintId}/history`,
        STATUSES: '/api/v1/projects/lookups/sprint-statuses',
      },
      // §14.1 Create, §14.2 List/Download, §14.3 Get-by-id, §14.4 Update,
      // §14.5 Delete (a real hard delete, unlike Sprint/Project's soft one),
      // §14.6 Upload / §14.7 Download / §14.8 Delete attachment(s).
      USER_STORIES: {
        LIST: projectId => `/api/v1/projects/${projectId}/user-stories/list`,
        CREATE: projectId => `/api/v1/projects/${projectId}/user-stories`,
        DETAIL: (projectId, userStoryId) =>
          `/api/v1/projects/${projectId}/user-stories/${userStoryId}`,
        UPDATE: (projectId, userStoryId) =>
          `/api/v1/projects/${projectId}/user-stories/${userStoryId}`,
        DELETE: (projectId, userStoryId) =>
          `/api/v1/projects/${projectId}/user-stories/${userStoryId}`,
        HISTORY: (projectId, userStoryId) =>
          `/api/v1/projects/${projectId}/user-stories/${userStoryId}/history`,
        // POST (upload, multipart) and DELETE (batch, ?attachmentIds=1,2)
        // both hit this same collection URL.
        ATTACHMENTS: (projectId, userStoryId) =>
          `/api/v1/projects/${projectId}/user-stories/${userStoryId}/attachments`,
        // GET (download bytes; ?inline=true for the view variant).
        ATTACHMENT: (projectId, userStoryId, attachmentId) =>
          `/api/v1/projects/${projectId}/user-stories/${userStoryId}/attachments/${attachmentId}`,
      },
      // §13 Status Management — one shared status list per project, used by
      // Sprints, User Stories, Tasks, and Bugs alike (no itemType).
      STATUSES: {
        LIST: projectId => `/api/v1/projects/${projectId}/statuses`,
        CREATE: projectId => `/api/v1/projects/${projectId}/statuses`,
        UPDATE: (projectId, statusId) =>
          `/api/v1/projects/${projectId}/statuses/${statusId}`,
        DELETE: (projectId, statusId) =>
          `/api/v1/projects/${projectId}/statuses/${statusId}`,
        REORDER: projectId => `/api/v1/projects/${projectId}/statuses/reorder`,
      },
      TASKS: {
        LIST: projectId => `/api/v1/projects/${projectId}/tasks/list`,
        CREATE: projectId => `/api/v1/projects/${projectId}/tasks`,
        DETAIL: (projectId, taskId) =>
          `/api/v1/projects/${projectId}/tasks/${taskId}`,
        UPDATE: (projectId, taskId) =>
          `/api/v1/projects/${projectId}/tasks/${taskId}`,
        DELETE: (projectId, taskId) =>
          `/api/v1/projects/${projectId}/tasks/${taskId}`,
        HISTORY: (projectId, taskId) =>
          `/api/v1/projects/${projectId}/tasks/${taskId}/history`,
        // §15.6/15.7 — POST (multipart upload, repeated `files`) and
        // DELETE (?attachmentIds=1,2) share this collection URL.
        ATTACHMENTS: (projectId, taskId) =>
          `/api/v1/projects/${projectId}/tasks/${taskId}/attachments`,
        // §15.8 — GET streams raw bytes (authenticated, IDOR-safe).
        ATTACHMENT_DOWNLOAD: (projectId, taskId, attachmentId) =>
          `/api/v1/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}/download`,
        LOOKUPS: {
          // §10 — Task uses the shared /lookups/priorities (P1-P4), not a
          // task-specific priorities endpoint; types/complexities are its own.
          PRIORITIES: '/api/v1/projects/lookups/priorities',
          TYPES: '/api/v1/projects/lookups/task-types',
          COMPLEXITIES: '/api/v1/projects/lookups/task-complexities',
        },
      },
      BUGS: {
        // §3 — list filters by taskId / userStoryId / sprintId (all optional,
        // independent). Used by the Task/User Story/Sprint detail Bugs tables.
        LIST: projectId => `/api/v1/projects/${projectId}/bugs/list`,
        // §16.3 — GET by id. §16.4 — PUT update (same URL).
        DETAIL: (projectId, bugId) =>
          `/api/v1/projects/${projectId}/bugs/${bugId}`,
        UPDATE: (projectId, bugId) =>
          `/api/v1/projects/${projectId}/bugs/${bugId}`,
        // §16.1 — Create a bug (bugNumber/owner/status are server-derived).
        CREATE: projectId => `/api/v1/projects/${projectId}/bugs`,
        // §16.5 — soft delete (same URL as DETAIL/UPDATE, DELETE verb).
        DELETE: (projectId, bugId) =>
          `/api/v1/projects/${projectId}/bugs/${bugId}`,
        HISTORY: (projectId, bugId) =>
          `/api/v1/projects/${projectId}/bugs/${bugId}/history`,
        // §16.6/16.7 — POST (multipart, repeated `files`) and DELETE
        // (?attachmentIds=1,2) share this collection URL.
        ATTACHMENTS: (projectId, bugId) =>
          `/api/v1/projects/${projectId}/bugs/${bugId}/attachments`,
        // §16.8 — GET streams raw bytes (authenticated, IDOR-safe).
        ATTACHMENT_DOWNLOAD: (projectId, bugId, attachmentId) =>
          `/api/v1/projects/${projectId}/bugs/${bugId}/attachments/${attachmentId}/download`,
      },
      BACKLOG: {
        // §17.1 — merged list of unassigned User Stories / Tasks / Bugs.
        LIST: projectId => `/api/v1/projects/${projectId}/backlog/list`,
      },
      COMMENTS: {
        LIST: (projectId, segment, targetId) =>
          `/api/v1/projects/${projectId}/${segment}/${targetId}/comments`,
        CREATE: (projectId, segment, targetId) =>
          `/api/v1/projects/${projectId}/${segment}/${targetId}/comments`,
        UPDATE: (projectId, segment, targetId, commentId) =>
          `/api/v1/projects/${projectId}/${segment}/${targetId}/comments/${commentId}`,
        DELETE: (projectId, segment, targetId, commentId) =>
          `/api/v1/projects/${projectId}/${segment}/${targetId}/comments/${commentId}`,
        ATTACHMENTS: (projectId, segment, targetId, commentId) =>
          `/api/v1/projects/${projectId}/${segment}/${targetId}/comments/${commentId}/attachments`,
        LIKES: (projectId, segment, targetId, commentId) =>
          `/api/v1/projects/${projectId}/${segment}/${targetId}/comments/${commentId}/likes`,
      },
      BOARD: {
        // §17 — Kanban board (Sprint work items grouped by assignee). POST.
        GET: projectId => `/api/v1/projects/${projectId}/board`,
      },
      INVOICES: {
        LIST: projectId => `/api/v1/projects/${projectId}/invoices/list`,
        CREATE: projectId => `/api/v1/projects/${projectId}/invoices`,
        DETAIL: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}`,
        UPDATE: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}`,
        DELETE: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}`,
        // GET (list, auto-syncs) and POST (add a manual billing hour, §8.4).
        LOGGED_HOURS: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/logged-hours`,
        GET_HOURS: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/logged-hours/get-hours`,
        // PUT (edit Charged Hours / Comments) and DELETE (manual rows only).
        LOGGED_HOUR: (projectId, invoiceId, rowId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/logged-hours/${rowId}`,
        LOGGED_HOURS_EXPORT: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/logged-hours/export`,
        // POST (multipart `files`), GET (list + count), DELETE (body { fileIds }).
        FILES: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/files`,
        FILE_DOWNLOAD: (projectId, invoiceId, fileId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/files/${fileId}/download`,
        SUBMIT: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/submit`,
        RECALL: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/recall`,
        APPROVAL_HISTORY: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/approval-history`,
        PRINTABLE: (projectId, invoiceId) =>
          `/api/v1/projects/${projectId}/invoices/${invoiceId}/printable`,
      },
      LOOKUPS: {
        PROJECT_TYPES: '/api/v1/projects/lookups/project-types',
        PROJECT_SUB_TYPES: '/api/v1/projects/lookups/project-sub-types',
        INVOICE_SCHEDULES: '/api/v1/projects/lookups/invoice-schedules',
        REVENUE_TYPES: '/api/v1/projects/lookups/revenue-types',
        INTERNAL_REVENUE_CATEGORIES:
          '/api/v1/projects/lookups/internal-revenue-categories',
        CURRENCIES: '/api/v1/projects/lookups/currencies',
        EXCHANGE_RATE: '/api/v1/projects/lookups/exchange-rate',
        STATUSES: '/api/v1/projects/lookups/statuses',
        PROJECT_ROLES: '/api/v1/projects/lookups/project-roles',
        // §10 — shared picklist (P1-P4) for User Story/Task/Bug priorityId.
        // Open to any authenticated user, not just PM/Super Admin.
        PRIORITIES: '/api/v1/projects/lookups/priorities',
        // §16 — Bug-only severity lookup (Critical / High / Medium / Low).
        SEVERITIES: '/api/v1/projects/lookups/severities',
        // Invoice-only status lookup (fixed set of 5, same roles as invoices).
        INVOICE_STATUSES: '/api/v1/projects/lookups/invoice-statuses',
      },
    },
    // Cross-project invoices for Accounts Admin / Super Admin.
    INVOICES: {
      LIST: '/api/v1/invoices/list',
      DETAIL: invoiceId => `/api/v1/invoices/${invoiceId}`,
      UPDATE: invoiceId => `/api/v1/invoices/${invoiceId}`,
      DELETE: invoiceId => `/api/v1/invoices/${invoiceId}`,
      APPROVE: invoiceId => `/api/v1/invoices/${invoiceId}/approve`,
      REJECT: invoiceId => `/api/v1/invoices/${invoiceId}/reject`,
      PROJECT_MANAGERS: '/api/v1/invoices/project-managers',
    },
    WORK_ANNIVERSARIES: {
      TODAY: '/api/v1/work-anniversaries/today',
      BY_DATE: date => `/api/v1/work-anniversaries/date/${date}`,
      DETAIL: cardId => `/api/v1/work-anniversaries/${cardId}`,
      ADD_WISH: cardId => `/api/v1/work-anniversaries/${cardId}/wishes`,
      DASHBOARD_TODAY: '/api/v1/work-anniversaries/dashboard/today',
      CREATE_TODAY: '/api/v1/work-anniversaries/create-today',
      REGENERATE: userId => `/api/v1/work-anniversaries/regenerate/${userId}`,
    },
    FINAL_RATINGS: {
      LIST: '/api/v1/final-ratings',
      SAVE_OR_UPDATE: '/api/v1/final-ratings/save-or-update',
      DETAIL: id => `/api/v1/final-ratings/${id}`,
      DETAIL_BY_EMPLOYEE_CYCLE: (employeeId, cycleId) =>
        `/api/v1/final-ratings/details/byEmployeeCycle?employeeId=${employeeId}&cycleId=${cycleId}`,
      UPDATE_BY_EMPLOYEE: (userId, cycleId) =>
        `/api/v1/final-ratings/update-by-employee/${userId}/${cycleId}`,
      UPDATE_BY_MANAGER: (employeeId, cycleId) =>
        `/api/v1/final-ratings/update-by-manager/${employeeId}/${cycleId}`,
      UPDATE_BY_HR: (employeeId, cycleId) =>
        `/api/v1/final-ratings/update-by-hr/${employeeId}/${cycleId}`,
      BY_EMPLOYEE_CYCLE: (employeeId, cycleId) =>
        `/api/v1/final-ratings/byEmployee_cycle?employeeId=${employeeId}&cycleId=${cycleId}`,
      CREATE: '/api/v1/final-ratings',
      UPDATE: id => `/api/v1/final-ratings/${id}`,
      DELETE: id => `/api/v1/final-ratings/${id}`,
    },
    ASSIGNED_GOALS: {
      LIST: '/api/v1/assigned-goals',
      CREATE: '/api/v1/assigned-goals',
      BULK_CREATE: '/api/v1/assigned-goals/bulk',
      BY_EMPLOYEE_AND_CYCLE: (employeeId, cycleId, page = 0, size = 10) =>
        `/api/v1/assigned-goals/by-employee-and-cycle?employeeId=${employeeId}&cycleId=${cycleId}&page=${page}&size=${size}`,
      BY_EMPLOYEE: employeeId =>
        `/api/v1/assigned-goals/by-employee?employeeId=${employeeId}`,
      DETAIL: id => `/api/v1/assigned-goals/${id}`,
    },
    GOAL_RATINGS: {
      DETAIL: id => `/api/v1/goal-ratings/${id}`,
      UPDATE: id => `/api/v1/goal-ratings/${id}`,
      CREATE: '/api/v1/goal-ratings',
      BY_ASSIGNMENT: assignedGoalId =>
        `/api/v1/goal-ratings/by-assignment?assignedGoalId=${assignedGoalId}`,
    },
    PERFORMANCE_DASHBOARD: '/api/v1/performance-dashboard/details',
    PERFORMANCE_DASHBOARD_DOWNLOAD: cycleId =>
      `/api/v1/performance-dashboard/download/${cycleId}`,
    PERFORMANCE_DASHBOARD_UPDATE_EMPLOYEE_STAGE_OVERRIDE: `/api/v1/performance-dashboard/updateEmployeeStageOverride`,
    PERFORMANCE_CYCLES: {
      LIST: '/api/v1/performance-cycles',
      YEARS: '/api/v1/performance-cycles/years',
      DETAIL: cycleId => `/api/v1/performance-cycles/${cycleId}`,
      DETAIL_BY_CYCLE_AND_EMPLOYEE: (cycleId, employeeId) =>
        `/api/v1/performance-cycles/cycle/${cycleId}/employee/${employeeId}`,
      CREATE: '/api/v1/performance-cycles',
      UPDATE: id => `/api/v1/performance-cycles/${id}`,
      DELETE: id => `/api/v1/performance-cycles/${id}`,
      STATUS_OPTIONS: '/api/v1/performance-cycles/stages',
      MY_CYCLES: userId => `/api/v1/performance-cycles/my-cycles/${userId}`,
      ALL: '/api/v1/performance-cycles/all',
      BY_EMPLOYEE: employeeId =>
        `/api/v1/performance-cycles/by-employee?employeeId=${employeeId}`,
      REVIEWER_ESCALATION: reviewerId =>
        `/api/v1/performance-cycles/reviewer-escalation?reviewerId=${reviewerId}`,
      GOAL_ACCEPTANCE_REQUEST_CHANGES: `/api/v1/performance-cycles/goal-acceptance/request-changes`,
    },
    GOALS: {
      LIST: '/api/v1/goals',
      DETAIL: id => `/api/v1/goals/${id}`,
      GET_BY_ID: id => `/api/v1/goals/getGoalById?id=${id}`,
      CREATE: '/api/v1/goals',
      UPDATE: id => `/api/v1/goals/${id}`,
      DELETE: id => `/api/v1/goals/${id}`,
    },
    REPORTEES: {
      LIST: '/api/v1/users/reportees',
    },
    MY_PERFORMANCE: {
      GET: (userId, cycleId) =>
        `/api/v1/final-ratings/my-performance?userId=${userId}&cycleId=${cycleId}`,
    },
    COMMENTS: {
      FOR_BIRTHDAY_CARD: cardId => `/api/v1/birthday-cards/${cardId}/wishes`,
      ADD_TO_BIRTHDAY_CARD: cardId => `/api/v1/birthday-cards/${cardId}/wishes`,
    },
    WORKANNIVERSYCOMMENTS: {
      FOR_WORKANNIVERSY_CARD: cardId =>
        `/api/v1/work-anniversaries/${cardId}/wishes`,
      ADD_TO_WORKANNIVERSY_CARD: cardId =>
        `/api/v1/work-anniversaries/${cardId}/wishes`,
    },
  },
  TIMEOUT: 30000,
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },
  SESSION_TIMEOUT_KEY,
};
export default API_CONFIG;
